import { Loader2, StickyNote } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useUpdateDeviceOperatorNote } from "./use-devices-query";

const MAX_NOTE_LEN = 500;

interface DeviceOperatorNoteProps {
	deviceUuid: string;
	operatorNote: string | null;
	/** `center` — środkowa kolumna kafelka (wypełnia pustą przestrzeń). */
	layout?: "stacked" | "center";
	/** Start od razu w edycji (np. z wiersza stanowiska w helpdesku). */
	autoEdit?: boolean;
	/** Koniec edycji (zapis, Esc, bez zmian) — wywołujący wraca do swojego widoku. */
	onDone?: () => void;
}

/**
 * Inline operator note on the device tile — muted one-liner when filled,
 * faint placeholder when empty. Click to edit; save on blur or Ctrl+Enter.
 */
export function DeviceOperatorNote({
	deviceUuid,
	operatorNote,
	layout = "stacked",
	autoEdit = false,
	onDone,
}: DeviceOperatorNoteProps) {
	const isCenter = layout === "center";
	const mutation = useUpdateDeviceOperatorNote();
	const [editing, setEditing] = useState(autoEdit);
	const [draft, setDraft] = useState(operatorNote ?? "");
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		if (!editing) {
			setDraft(operatorNote ?? "");
		}
	}, [operatorNote, editing]);

	useEffect(() => {
		if (editing) {
			textareaRef.current?.focus();
			textareaRef.current?.select();
		}
	}, [editing]);

	function finish() {
		setEditing(false);
		onDone?.();
	}

	function cancel() {
		setDraft(operatorNote ?? "");
		finish();
	}

	function save() {
		const trimmed = draft.trim();
		const current = (operatorNote ?? "").trim();
		if (trimmed === current) {
			finish();
			return;
		}
		mutation.mutate(
			{ deviceUuid, operatorNote: draft },
			{
				onSuccess: finish,
				onError: () => toast.error("Nie udało się zapisać notatki"),
			},
		);
	}

	function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
		if (e.key === "Escape") {
			e.preventDefault();
			cancel();
		}
		if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
			e.preventDefault();
			save();
		}
	}

	if (editing) {
		return (
			<div className={cn("space-y-1", !isCenter && "mt-1")}>
				<textarea
					ref={textareaRef}
					value={draft}
					onChange={(e) => setDraft(e.target.value.slice(0, MAX_NOTE_LEN))}
					onBlur={() => {
						if (!mutation.isPending) save();
					}}
					onKeyDown={onKeyDown}
					rows={2}
					maxLength={MAX_NOTE_LEN}
					disabled={mutation.isPending}
					placeholder="Np. stanowisko przy oknie, drugi monitor…"
					className={cn(
						"w-full resize-none rounded-sm border border-border/50 bg-muted/25 px-2 py-1",
						"text-xs leading-relaxed text-foreground placeholder:text-muted-foreground/50",
						"focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/60",
					)}
					aria-label="Notatka do stanowiska"
				/>
				<p className="text-[10px] text-muted-foreground/60">
					{mutation.isPending ? (
						<span className="inline-flex items-center gap-1">
							<Loader2 className="h-3 w-3 animate-spin" aria-hidden />
							Zapisuję…
						</span>
					) : (
						"Ctrl+Enter zapisuje · Esc anuluje"
					)}
				</p>
			</div>
		);
	}

	if (operatorNote?.trim()) {
		return (
			<button
				type="button"
				onClick={() => setEditing(true)}
				aria-label={`Edytuj notatkę: ${operatorNote}`}
				className={cn(
					"group flex w-full gap-1.5 text-left text-xs transition-colors",
					isCenter
						? "items-center rounded-md border border-border/40 bg-muted/20 px-2 py-1.5 text-muted-foreground hover:border-border/70 hover:bg-muted/35"
						: "mt-1 items-start rounded-sm px-0.5 py-0.5 text-muted-foreground/75 hover:bg-muted/35 hover:text-muted-foreground max-md:min-h-9 max-md:items-center",
				)}
				title="Kliknij, aby edytować notatkę"
			>
				<StickyNote
					className={cn(
						"h-3 w-3 shrink-0 opacity-40 group-hover:opacity-60",
						isCenter ? "" : "mt-0.5",
					)}
					aria-hidden
				/>
				<span
					className={cn(
						"min-w-0 whitespace-pre-wrap break-words",
						isCenter ? "line-clamp-1" : "line-clamp-2",
					)}
				>
					{operatorNote}
				</span>
			</button>
		);
	}

	return (
		<button
			type="button"
			onClick={() => setEditing(true)}
			aria-label="Dodaj notatkę do stanowiska"
			className={cn(
				"w-full text-xs transition-colors",
				isCenter
					? "truncate rounded-md border border-dashed border-border/45 px-2 py-1.5 text-center text-muted-foreground/45 hover:border-border/70 hover:bg-muted/15 hover:text-muted-foreground/70"
					: "mt-0.5 text-left text-muted-foreground/40 hover:text-muted-foreground/70 max-md:min-h-9",
			)}
		>
			Dodaj notatkę…
		</button>
	);
}
